/**
 * Per-booking pickup / drop-off logistics card. Renders the host's default
 * instructions (read-only, mirrors `/vendors/:id/policies`) plus an editable
 * "Note for this booking" field. Either side of the booking — customer OR
 * vendor — can edit the note; the side viewing sees their counterpart's note
 * inline.
 *
 * Backed by:
 *   GET  /vendors/:id/policies          → host defaults + map URLs (public)
 *   PATCH /bookings/:id/logistics-notes → write the per-booking notes
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Navigation, ExternalLink, Loader2, Save, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { toast } from "sonner";

/** Mirror of the backend `_resolveMapUrl` parser: only iframe `google.com/maps/embed*`. */
function resolveMapUrl(raw) {
  if (!raw || typeof raw !== "string") return { ok: false };
  let url;
  try { url = new URL(raw.trim()); } catch { return { ok: false }; }
  if (!["http:", "https:"].includes(url.protocol)) return { ok: false };
  const host = url.host.toLowerCase();
  const isGoogleMaps = host === "google.com" || host.endsWith(".google.com") || host === "maps.google.com";
  const isEmbed = isGoogleMaps && /\/maps\/embed/i.test(url.pathname);
  return { ok: true, isEmbed, isGoogleMaps, href: url.toString() };
}

/** Lightweight "x minutes ago" formatter — keeps the dep tree small.
 *  Returns "just now", "5 min ago", "2 h ago", "3 d ago", or an absolute date for >30d. */
function formatRelativeTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 45) return "just now";
  if (diffSec < 60 * 60) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 60 * 60 * 24) return `${Math.floor(diffSec / 3600)} h ago`;
  if (diffSec < 60 * 60 * 24 * 30) return `${Math.floor(diffSec / 86400)} d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/** Resolve who edited the notes last → "you" / "host" / "customer" / "someone". */
function resolveEditor(editedBy, currentUserId, customerId, vendorId) {
  if (!editedBy) return null;
  if (editedBy === currentUserId) return "you";
  if (editedBy === vendorId) return "host";
  if (editedBy === customerId) return "customer";
  return "someone";
}

export default function BookingLogisticsCard({ booking, vendorId, onSaved }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState(null);     // { pickup_instructions, pickup_map_url, dropoff_instructions, dropoff_map_url }
  const [pickupNote, setPickupNote] = useState(booking?.pickup_notes || "");
  const [dropoffNote, setDropoffNote] = useState(booking?.dropoff_notes || "");
  // Per-booking map URL overrides — both customer and vendor can edit.
  const [pickupMapOverride, setPickupMapOverride] = useState(booking?.pickup_map_url || "");
  const [dropoffMapOverride, setDropoffMapOverride] = useState(booking?.dropoff_map_url || "");
  const [saving, setSaving] = useState(false);
  // Local override so the chip refreshes instantly after Save (the dashboard's
  // `load()` is async — props would catch up a moment later otherwise).
  const [edited, setEdited] = useState({
    at: booking?.logistics_notes_edited_at || null,
    by: booking?.logistics_notes_edited_by || null,
  });
  useEffect(() => {
    setEdited({
      at: booking?.logistics_notes_edited_at || null,
      by: booking?.logistics_notes_edited_by || null,
    });
  }, [booking?.logistics_notes_edited_at, booking?.logistics_notes_edited_by]);

  useEffect(() => {
    if (!open || !vendorId || host !== null) return;
    api.get(`/vendors/${vendorId}/policies`).then((r) => {
      setHost(r.data?.logistics || {});
    }).catch(() => setHost({}));
  }, [open, vendorId, host]);

  useEffect(() => {
    setPickupNote(booking?.pickup_notes || "");
    setDropoffNote(booking?.dropoff_notes || "");
    setPickupMapOverride(booking?.pickup_map_url || "");
    setDropoffMapOverride(booking?.dropoff_map_url || "");
  }, [booking?.pickup_notes, booking?.dropoff_notes, booking?.pickup_map_url, booking?.dropoff_map_url]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/bookings/${booking.id}/logistics-notes`, {
        pickup_notes: pickupNote,
        dropoff_notes: dropoffNote,
        pickup_map_url: pickupMapOverride,
        dropoff_map_url: dropoffMapOverride,
      });
      setEdited({ at: data?.edited_at || null, by: data?.edited_by || null });
      toast.success("Logistics notes saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Compact teaser when collapsed; the card never reserves vertical space when there's
  // nothing for the user to see.
  const hasContent =
    booking?.pickup_notes || booking?.dropoff_notes ||
    (host && (host.pickup_instructions || host.dropoff_instructions || host.pickup_map_url || host.dropoff_map_url));

  const differentDropoff =
    (booking?.pickup_location || "").trim() &&
    (booking?.dropoff_location || "").trim() &&
    booking.pickup_location.trim() !== booking.dropoff_location.trim();

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/30 px-3 py-2 mt-2" data-testid={`logistics-card-${booking?.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-xs font-medium text-blue-900 hover:text-blue-700 w-full"
        data-testid={`logistics-toggle-${booking?.id}`}
      >
        <Navigation className="h-3.5 w-3.5" />
        Pickup & drop-off details
        {open ? <ChevronUp className="h-3.5 w-3.5 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 ml-auto" />}
      </button>
      {open && (
        <div className="mt-3" data-testid={`logistics-body-${booking?.id}`}>
          {differentDropoff && (
            <div
              className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-900"
              data-testid={`logistics-different-dropoff-${booking?.id}`}
            >
              Drop-off is at a different location than pickup — feel free to drop a Google Maps link below so {user?.role === "vendor" ? "the customer" : "the host"} knows exactly where to meet.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LogisticsHalf
              testIdSuffix="pickup"
              title="Pickup"
              icon={<MapPin className="h-3.5 w-3.5 text-emerald-700" />}
              location={booking?.pickup_location}
              instructions={host?.pickup_instructions}
              hostMapUrl={host?.pickup_map_url}
              mapOverride={pickupMapOverride}
              setMapOverride={setPickupMapOverride}
              note={pickupNote}
              setNote={setPickupNote}
              bookingId={booking?.id}
            />
            <LogisticsHalf
              testIdSuffix="dropoff"
              title="Drop-off"
              icon={<MapPin className="h-3.5 w-3.5 text-rose-700" />}
              location={booking?.dropoff_location}
              instructions={host?.dropoff_instructions}
              hostMapUrl={host?.dropoff_map_url}
              mapOverride={dropoffMapOverride}
              setMapOverride={setDropoffMapOverride}
              note={dropoffNote}
              setNote={setDropoffNote}
              bookingId={booking?.id}
            />
          </div>
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[10px] text-neutral-500">
                Notes are visible to {booking?.customer_name ? "both you and the host" : "you and the customer"} and appear in the rental agreement.
              </span>
              {edited.at && edited.by && (() => {
                const editorRole = resolveEditor(edited.by, user?.id, booking?.customer_id, booking?.vendor_id);
                const rel = formatRelativeTime(edited.at);
                if (!editorRole || !rel) return null;
                const label =
                  editorRole === "you" ? `Edited by you · ${rel}`
                  : editorRole === "host" ? `Edited by host · ${rel}`
                  : editorRole === "customer" ? `Edited by customer · ${rel}`
                  : `Edited · ${rel}`;
                return (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200"
                    title={new Date(edited.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                    data-testid={`logistics-edited-chip-${booking?.id}`}
                  >
                    <Clock className="h-3 w-3 opacity-70" />
                    {label}
                  </span>
                );
              })()}
            </div>
            <Button size="sm" onClick={save} disabled={saving} className="rounded-full h-7 text-xs" data-testid={`logistics-save-${booking?.id}`}>
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save notes
            </Button>
          </div>
        </div>
      )}
      {!open && hasContent && (
        <div className="text-[10px] text-neutral-500 mt-1" data-testid={`logistics-summary-${booking?.id}`}>
          {booking?.pickup_notes || booking?.dropoff_notes ? "Custom notes on this booking" : "Host has shared pickup details"}
        </div>
      )}
    </div>
  );
}

function LogisticsHalf({ testIdSuffix, title, icon, location, instructions, hostMapUrl, mapOverride, setMapOverride, note, setNote, bookingId }) {
  // Effective URL = per-booking override beats host default. We RENDER the override when set,
  // otherwise fall back to the host's URL so users still see the host's map when nothing
  // custom has been added.
  const effectiveUrl = (mapOverride || "").trim() || (hostMapUrl || "").trim();
  const map = resolveMapUrl(effectiveUrl);
  const usingOverride = (mapOverride || "").trim().length > 0;
  return (
    <div className="rounded-lg bg-white border border-border p-3 text-xs" data-testid={`logistics-half-${testIdSuffix}-${bookingId}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="font-semibold uppercase tracking-wider text-neutral-800">{title}</span>
      </div>
      {location && (
        <div className="text-[10px] text-neutral-500 mb-1.5" data-testid={`logistics-location-${testIdSuffix}-${bookingId}`}>
          <span className="uppercase tracking-wider font-semibold">Location:</span> {location}
        </div>
      )}
      {instructions ? (
        <div className="text-neutral-700 whitespace-pre-wrap leading-relaxed" data-testid={`logistics-host-${testIdSuffix}-${bookingId}`}>{instructions}</div>
      ) : (
        <div className="text-neutral-400 italic">Host hasn't shared {title.toLowerCase()} instructions yet.</div>
      )}
      {map.ok && map.isEmbed && (
        <iframe
          src={map.href}
          title={`${title} map`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
          className="mt-2 w-full h-32 rounded-md border border-border"
          data-testid={`logistics-map-${testIdSuffix}-${bookingId}`}
        />
      )}
      {map.ok && !map.isEmbed && (
        <a
          href={map.href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1 text-blue-700 hover:underline"
          data-testid={`logistics-link-${testIdSuffix}-${bookingId}`}
        >
          <ExternalLink className="h-3 w-3" /> Open map
        </a>
      )}
      <label className="block mt-3">
        <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1">
          Custom map URL for this booking
          {usingOverride && (
            <span
              className="bg-blue-100 text-blue-800 text-[9px] px-1.5 py-[1px] rounded-full font-bold"
              title="Per-booking override is active (replaces the host's default map)."
            >
              OVERRIDE
            </span>
          )}
        </span>
        <input
          type="url"
          value={mapOverride}
          onChange={(e) => setMapOverride(e.target.value)}
          placeholder="https://www.google.com/maps/embed?pb=… or any Google Maps share link"
          maxLength={500}
          className="mt-1 w-full rounded-md border border-input bg-white px-2 py-1.5 text-xs placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          data-testid={`logistics-map-url-${testIdSuffix}-${bookingId}`}
        />
        <span className="text-[10px] text-neutral-400 mt-0.5 block">
          {hostMapUrl ? "Leave blank to keep the host's default map." : "Add a Google Maps URL so the other side knows exactly where to meet."}
        </span>
      </label>
      <label className="block mt-3">
        <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Note for this booking</span>
        <Textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={`Add any ${title.toLowerCase()}-specific details, e.g. arrival time, terminal, gate…`}
          maxLength={1000}
          className="mt-1 rounded-md text-xs"
          data-testid={`logistics-note-${testIdSuffix}-${bookingId}`}
        />
      </label>
    </div>
  );
}
